import { useEffect } from 'react';

interface ArticleSEOData {
  title: string;
  description: string;
  slug: string;
  datePublished: string;
  imageUrl?: string;
}

interface SEOMetaProps {
  type: 'homepage' | 'article';
  articleData?: ArticleSEOData;
  pageTitle?: string;
}

export default function SEOMeta({ type, articleData, pageTitle }: SEOMetaProps) {
  const siteUrl: string =
    typeof import.meta !== 'undefined' &&
    import.meta.env &&
    import.meta.env.VITE_SITE_URL
      ? String(import.meta.env.VITE_SITE_URL)
      : 'https://oopbuy.boutique';

  useEffect(() => {
    if (pageTitle) {
      document.title = pageTitle;
    } else if (type === 'homepage') {
      document.title =
        'OOPBuy Spreadsheet — The Most Advanced Product Sourcing Spreadsheet System | oopbuy.boutique';
    } else if (type === 'article' && articleData) {
      document.title = `${articleData.title} | oopbuy.boutique`;
    }

    // Remove previous JSON-LD scripts
    document.querySelectorAll('script[data-seo-jsonld]').forEach((s) => s.remove());

    if (type === 'homepage') {
      const websiteScript = document.createElement('script');
      websiteScript.type = 'application/ld+json';
      websiteScript.setAttribute('data-seo-jsonld', 'true');
      websiteScript.textContent = JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'WebSite',
        name: 'OOPBuy Spreadsheet',
        url: siteUrl,
        description:
          'The most advanced product sourcing spreadsheet system for structured ecommerce product comparison and data-driven shopping decisions.',
        potentialAction: {
          '@type': 'SearchAction',
          target: {
            '@type': 'EntryPoint',
            urlTemplate: `${siteUrl}/?q={search_term_string}`,
          },
          'query-input': 'required name=search_term_string',
        },
      });
      document.head.appendChild(websiteScript);

      const orgScript = document.createElement('script');
      orgScript.type = 'application/ld+json';
      orgScript.setAttribute('data-seo-jsonld', 'true');
      orgScript.textContent = JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'Organization',
        name: 'OOPBuy',
        url: siteUrl,
        description:
          'Structured ecommerce product sourcing and comparison platform providing spreadsheet-based tools for efficient product research and data-driven shopping decisions.',
        sameAs: ['https://kaspreadsheet.com/'],
      });
      document.head.appendChild(orgScript);
    } else if (type === 'article' && articleData) {
      const articleJson: Record<string, unknown> = {
        '@context': 'https://schema.org',
        '@type': 'Article',
        headline: articleData.title,
        description: articleData.description,
        url: `${siteUrl}/${articleData.slug}`,
        datePublished: articleData.datePublished,
        dateModified: articleData.datePublished,
        author: {
          '@type': 'Organization',
          name: 'OOPBuy Editorial Team',
        },
        publisher: {
          '@type': 'Organization',
          name: 'OOPBuy',
          url: siteUrl,
        },
        mainEntityOfPage: {
          '@type': 'WebPage',
          '@id': `${siteUrl}/${articleData.slug}`,
        },
      };

      if (articleData.imageUrl) {
        articleJson.image = articleData.imageUrl;
      }

      const articleScript = document.createElement('script');
      articleScript.type = 'application/ld+json';
      articleScript.setAttribute('data-seo-jsonld', 'true');
      articleScript.textContent = JSON.stringify(articleJson);
      document.head.appendChild(articleScript);
    }
  }, [type, articleData, pageTitle, siteUrl]);

  return null;
}